import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../Assets/logo.png';

export default function ProcessingStatus() {
  const [currentFile, setCurrentFile] = useState(null);
  const [allFiles, setAllFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [procedures, setProcedures] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [currentModalUuid, setCurrentModalUuid] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkServerHealth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_APP_URL}/health`);
        return response.ok;
      } catch (error) {
        return false;
      }
    };

    const fetchWithRetry = async (url, maxRetries = 3) => {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          // Add headers to bypass ngrok browser warning and prevent caching
          const headers = {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          };
          if (url.includes('ngrok')) {
            headers['ngrok-skip-browser-warning'] = 'true';
            headers['User-Agent'] = 'VisageAI-Frontend/1.0';
          }
          
          const response = await fetch(url, { headers });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const text = await response.text();
          
          return JSON.parse(text);
        } catch (error) {
          
          if (attempt === maxRetries) {
            throw error;
          }
          
          // Wait before retrying (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    };

    const fetchData = async () => {
      const cellphone = localStorage.getItem('cellphone');
      const th = localStorage.getItem('th');
      const uuid_value = localStorage.getItem('current_task_id');
      const name = localStorage.getItem('client_name');

      if (!cellphone || !th) {
        
        // Try to recover th from URL if available
        const urlParams = new URLSearchParams(window.location.search);
        const thFromUrl = urlParams.get('th');
        if (thFromUrl && !th) {
          localStorage.setItem('th', thFromUrl);
          window.location.reload();
          return;
        }
        
        // Retry mechanism - wait and try again before redirecting
        if (retryCount < 3 && (!th || !cellphone)) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000);
          return;
        }
        
        navigate('/');
        return;
      }

      // Check server health before making requests
      await checkServerHealth();

      try {
        // Get company info with retry logic
        const data = await fetchWithRetry(`${process.env.REACT_APP_BACKEND_APP_URL}/company/${th}`);
        const company_name = data.name;

        // Only send webhook if we have a uuid_value
        if (uuid_value) {
          // 🔔 Envio do webhook (non-blocking)
          fetch('https://webhook.sofiq.com.br/webhook/ef36126a-8c8b-4dea-a5e3-223daa7d4b3a', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              event: 'VisageAI',
              company_name,
              cellphone,
              name,
              uuid: uuid_value,
            }),
          }).catch(err => {
            // Webhook error - non-blocking
          });

          // 🔍 Fetch current file if uuid exists
          try {
            const currentData = await fetchWithRetry(`${process.env.REACT_APP_BACKEND_AI_URL}/files_by_uuid?cellphone=${cellphone}&uuid_value=${uuid_value}&th=${th}`);
            setCurrentFile(currentData.files);
          } catch (error) {
            setCurrentFile(null);
          }
        } else {
          setCurrentFile(null);
        }
        
        // Always fetch all historical files
        try {
          const allData = await fetchWithRetry(`${process.env.REACT_APP_BACKEND_AI_URL}/files_by_cellphone?cellphone=${cellphone}&th=${th}`);
          setAllFiles(allData.files || {});
        } catch (error) {
          setAllFiles({});
        }
      } catch (error) {
        // Error fetching files - just show error state
        setCurrentFile(null);
        setAllFiles({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, retryCount]);

  const fetchProcedures = async (uuid_value) => {
    const cellphone = localStorage.getItem('cellphone');
    const th = localStorage.getItem('th');

    try {
      const url = `${process.env.REACT_APP_BACKEND_AI_URL}/procedures_by_uuid?cellphone=${cellphone}&uuid_value=${uuid_value}&th=${th}`;
      
      // Add headers to bypass ngrok browser warning
      const headers = {};
      if (url.includes('ngrok')) {
        headers['ngrok-skip-browser-warning'] = 'true';
        headers['User-Agent'] = 'VisageAI-Frontend/1.0';
      }
      
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error('Erro ao buscar procedimentos');
      const data = await response.json();
      
      // Try different possible property names for procedures
      let proceduresList = data.procedimentos || 
                          data.procedures || 
                          data.recommendations || 
                          data.results || 
                          data.data || 
                          [];
                          
      // If it's nested, try to extract array
      if (!Array.isArray(proceduresList) && typeof proceduresList === 'object') {
        proceduresList = Object.values(proceduresList).find(val => Array.isArray(val)) || [];
      }
      
      setProcedures(proceduresList);
      setCarouselIndex(0);
      setCurrentModalUuid(uuid_value);
      setShowModal(true);
    } catch (err) {
      // Error fetching procedures
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#5C2D91] via-[#7B4397] to-[#00B1B1] flex flex-col items-center justify-center px-6">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-3 border-[#00B1B1] mx-auto mb-4"></div>
          <p className="text-[#5C2D91] font-semibold text-lg mb-2">Carregando seus resultados...</p>
          <p className="text-gray-600 text-sm">Estamos preparando sua análise personalizada</p>
        </div>
      </div>
    );
  }

  // If no current file and no historical files, show welcome screen
  if (!currentFile && Object.keys(allFiles).length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#5C2D91] to-[#00B1B1] px-6 py-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">Bem-vindo ao VisageAI</h1>
              <p className="text-white/90 text-sm">Comece sua análise personalizada</p>
            </div>
            <img src={logo} alt="VisageAI logo" className="w-12 h-12 object-contain" />
          </div>
        </div>

        <div className="flex flex-col items-center justify-center px-6 py-12">
          <div className="bg-white rounded-3xl shadow-xl p-8 text-center max-w-sm">
            <div className="w-16 h-16 bg-[#00B1B1]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-[#00B1B1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Nenhuma análise encontrada</h2>
            <p className="text-gray-600 text-sm mb-6">Faça sua primeira análise facial com IA para receber recomendações personalizadas</p>
            <button 
              onClick={() => navigate('/capture-cam')} 
              className="w-full bg-gradient-to-r from-[#00B1B1] to-[#00A0A0] hover:from-[#00A0A0] hover:to-[#009999] text-white border-none rounded-2xl py-4 px-6 font-semibold text-base transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Iniciar Primeira Análise
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#5C2D91] to-[#00B1B1] px-6 py-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Seus Resultados</h1>
            <p className="text-white/90 text-sm">Análise VisageAI completa</p>
          </div>
          <img src={logo} alt="VisageAI logo" className="w-12 h-12 object-contain" />
        </div>
      </div>

      <div className="px-6 py-6 space-y-6">
        {/* Current Result Card - Only show if currentFile exists */}
        {currentFile && (
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-[#00B1B1] rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Resultado Mais Recente</h2>
                  <p className="text-gray-600 text-sm">Toque para ver recomendações</p>
                </div>
              </div>
              
              <button 
                className="w-full group max-w-sm mx-auto" 
                onClick={() => fetchProcedures(localStorage.getItem('current_task_id'))}
              >
                <div className="relative overflow-hidden rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-300">
                  <img 
                    src={currentFile.before_after} 
                    alt="Antes e Depois" 
                    className="w-full h-auto max-h-64 object-cover group-hover:scale-105 transition-transform duration-300" 
                  />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3">
                    <p className="text-[#5C2D91] font-medium text-sm">👆 Toque para ver procedimentos recomendados</p>
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* Action Button */}
          <div className="p-6 pt-0">
            <button 
              onClick={() => navigate('/capture-cam')} 
              className="w-full bg-gradient-to-r from-[#00B1B1] to-[#00A0A0] hover:from-[#00A0A0] hover:to-[#009999] text-white border-none rounded-2xl py-4 px-6 font-semibold text-base transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Nova Análise
            </button>
          </div>
        </div>
        )}

        {/* Call to Action when no current result */}
        {!currentFile && Object.keys(allFiles).length > 0 && (
          <div className="bg-white rounded-3xl shadow-xl p-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#00B1B1]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-[#00B1B1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Fazer Nova Análise</h3>
              <p className="text-gray-600 text-sm mb-4">Capture uma nova foto para análise atualizada</p>
              <button 
                onClick={() => navigate('/capture-cam')} 
                className="w-full bg-gradient-to-r from-[#00B1B1] to-[#00A0A0] hover:from-[#00A0A0] hover:to-[#009999] text-white border-none rounded-2xl py-3 px-5 font-semibold text-base transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl"
              >
                Iniciar Captura
              </button>
            </div>
          </div>
        )}

        {/* Previous Results */}
        {Object.keys(allFiles).length > 0 && (
          <div className="bg-white rounded-3xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#5C2D91] rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Histórico de Análises</h3>
                <p className="text-gray-600 text-sm">{Object.keys(allFiles).length} resultado{Object.keys(allFiles).length !== 1 ? 's' : ''} anterior{Object.keys(allFiles).length !== 1 ? 'es' : ''}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(allFiles).map(([uuid, fileObj]) => (
                fileObj.before_after && (
                  <button
                    key={uuid}
                    className="group relative overflow-hidden rounded-xl aspect-[4/3] bg-gray-100 shadow-md hover:shadow-lg transition-all duration-300 max-h-24"
                    onClick={() => fetchProcedures(uuid)}
                  >
                    <img
                      src={fileObj.before_after}
                      alt={`Análise ${uuid.slice(0, 6)}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1">
                        <p className="text-[#5C2D91] font-medium text-xs truncate">{uuid.slice(0, 8)}...</p>
                      </div>
                    </div>
                  </button>
                )
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-fade-in" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#5C2D91] to-[#00B1B1] px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Recomendações</h3>
                  <p className="text-white/80 text-sm">Procedimentos sugeridos para você</p>
                </div>
                <button 
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Before and After Photos */}
            {currentModalUuid && (allFiles[currentModalUuid] || (currentFile && currentModalUuid === localStorage.getItem('current_task_id'))) && (
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
                <h4 className="text-sm font-medium text-gray-700 mb-3 text-center">Antes e Depois</h4>
                <div className="max-w-xs mx-auto">
                  <img 
                    src={currentModalUuid === localStorage.getItem('current_task_id') ? currentFile?.before_after : allFiles[currentModalUuid]?.before_after}
                    alt="Antes e Depois" 
                    className="w-full h-auto rounded-xl shadow-lg"
                  />
                </div>
              </div>
            )}

            {/* Modal Content */}
            <div className="p-6">
              {procedures.length > 0 && procedures[carouselIndex] ? (
                <div className="space-y-6">
                  {/* Debug info */}
                  <div className="hidden">
                    <pre className="text-xs bg-gray-100 p-2 rounded">
                      {JSON.stringify(procedures[carouselIndex], null, 2)}
                    </pre>
                  </div>
                  
                  {/* Procedure Card */}
                  <div className="text-center">
                    <div className="relative mb-4">
                      {procedures[carouselIndex].name_pt ? (
                        <>
                          <img
                            src={`/${procedures[carouselIndex].name_pt.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '')}.png`}
                            alt={procedures[carouselIndex].name_pt}
                            className="w-full h-48 object-cover rounded-2xl shadow-lg"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-full h-48 bg-gradient-to-br from-[#5C2D91]/20 to-[#00B1B1]/20 rounded-2xl hidden items-center justify-center flex-col gap-3">
                            {procedures[carouselIndex].name_pt.toLowerCase().includes('botox') ? (
                              <>
                                <div className="w-16 h-16 bg-white/80 rounded-full flex items-center justify-center">
                                  <svg className="w-10 h-10 text-[#5C2D91]" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M11 15H13V17H11V15M11 7H13V13H11V7M12 2C6.47 2 2 6.5 2 12A10 10 0 0 0 12 22A10 10 0 0 0 22 12A10 10 0 0 0 12 2M12 20A8 8 0 0 1 4 12A8 8 0 0 1 12 4A8 8 0 0 1 20 12A8 8 0 0 1 12 20Z" />
                                  </svg>
                                </div>
                                <p className="text-[#5C2D91] font-semibold">Botox</p>
                              </>
                            ) : (
                              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-48 bg-gradient-to-br from-[#5C2D91]/20 to-[#00B1B1]/20 rounded-2xl flex items-center justify-center">
                          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                      {procedures[carouselIndex].name_pt || procedures[carouselIndex].name || 'Procedimento'}
                    </h4>
                    <div className="bg-[#00B1B1]/10 rounded-xl p-3 mb-4">
                      <p className="text-[#00B1B1] font-medium text-sm">
                        {procedures[carouselIndex].area_pt || procedures[carouselIndex].area || 'Área não especificada'}
                      </p>
                    </div>
                  </div>

                  {/* Navigation */}
                  {procedures.length > 1 && (
                    <div className="flex items-center justify-between">
                      <button 
                        className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors" 
                        onClick={() => setCarouselIndex((carouselIndex - 1 + procedures.length) % procedures.length)}
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 mr-2">{carouselIndex + 1}/{procedures.length}</span>
                        {procedures.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCarouselIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all ${index === carouselIndex ? 'bg-[#00B1B1] scale-125 w-6' : 'bg-gray-300 hover:bg-gray-400'}`}
                            aria-label={`Go to procedure ${index + 1}`}
                          />
                        ))}
                      </div>
                      
                      <button 
                        className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors" 
                        onClick={() => setCarouselIndex((carouselIndex + 1) % procedures.length)}
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 font-medium">Nenhuma recomendação encontrada</p>
                  <p className="text-gray-500 text-sm mt-1">Tente fazer uma nova análise</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
